import type { ReactNode } from 'react'
import { PexIcon } from '../icons/PexIcon'
import { Button } from '../ui/button'
import { Select } from '../ui/select'

export type MobileOperationsQueue<Q extends string = string> = {
  value: Q
  label: string
}

export type MobileOperationsHeaderProps<Q extends string = string> = {
  queueLabel: string
  queue: Q
  queues: ReadonlyArray<MobileOperationsQueue<Q>>
  total: number
  totalLabel: string
  searchOpen: boolean
  searchLabel: string
  closeSearchLabel: string
  filterLabel: string
  activeFilterCount: number
  onQueueChange: (queue: Q) => void
  onSearchToggle: () => void
  onFilterOpen: () => void
  primaryAction?: ReactNode
  moreActions?: ReactNode
  searchField?: ReactNode
  sortControl?: ReactNode
  activeFilters?: ReactNode
}

export function MobileOperationsHeader<Q extends string>({
  queueLabel,
  queue,
  queues,
  total,
  totalLabel,
  searchOpen,
  searchLabel,
  closeSearchLabel,
  filterLabel,
  activeFilterCount,
  onQueueChange,
  onSearchToggle,
  onFilterOpen,
  primaryAction,
  moreActions,
  searchField,
  sortControl,
  activeFilters,
}: MobileOperationsHeaderProps<Q>) {
  const filterAccessibleLabel =
    activeFilterCount > 0
      ? `${filterLabel} (${activeFilterCount})`
      : filterLabel

  return (
    <div data-testid="mobile-operations-header">
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5">
        <label htmlFor="mobile-operations-queue" className="sr-only">
          {queueLabel}
        </label>
        <div className="flex items-center gap-2">
          <Select
            id="mobile-operations-queue"
            aria-label={queueLabel}
            className="border-0 bg-transparent px-1 text-base font-semibold shadow-none"
            value={queue}
            onChange={(event) => onQueueChange(event.target.value as Q)}
          >
            {queues.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <span className="shrink-0 rounded-full bg-[var(--color-surface-muted)] px-2 py-1 text-xs font-semibold tabular-nums text-[var(--color-text-muted)]">
            {total}
          </span>
        </div>
      </div>

      <div className="flex min-h-14 items-center gap-1.5 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 [&_button]:min-h-11 [&_button]:sm:min-h-11">
        <span className="mr-auto whitespace-nowrap text-sm font-semibold">
          {totalLabel}
        </span>
        <Button
          type="button"
          variant={searchOpen ? 'primary' : 'ghost'}
          size="icon"
          aria-label={searchOpen ? closeSearchLabel : searchLabel}
          aria-expanded={searchOpen}
          onClick={onSearchToggle}
        >
          <PexIcon
            name={searchOpen ? 'close' : 'search'}
            size="sm"
            decorative
          />
        </Button>
        <Button
          type="button"
          variant="outline"
          className="gap-1.5 px-3"
          aria-label={filterAccessibleLabel}
          onClick={onFilterOpen}
        >
          <PexIcon name="filter" size="sm" decorative />
          <span className="text-xs font-semibold">{filterLabel}</span>
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-[var(--color-action)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--color-action-foreground)]">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {primaryAction}
        {moreActions}
      </div>

      {searchOpen && searchField && (
        <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
          {searchField}
        </div>
      )}
      {sortControl}
      {activeFilters}
    </div>
  )
}
